import * as React from 'react';
import {Mutation} from 'react-apollo';
import {DragDropContext, Droppable} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';

import type {
    RepositionStagesetInput,
    RepositionStageset as RepositionStagesetMutation,
    RepositionStagesetVariables as RepositionStagesetMutationVariables,
    StagesetsForPipelineList as StagesetsForPipelineListQuery,
    StagesetBasic as StagesetBasicFragment,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../apollo-client';

import REPOSITION_STAGESET from '../graphql/mutations/reposition-stageset.graphql';
import GET_STAGESETS from '../graphql/queries/get-stagesets-for-pipeline-list.graphql';

import {DraggablePipelineListItem} from './draggable-pipeline-list-item';
import {PipelineListItem} from './pipeline-list-item';

type OwnProps = {
    stagesets: StagesetBasicFragment[];
};

type Props = OwnProps & {
    repositionStageset: (arg1: RepositionStagesetInput) => Promise<any>;
};

export class DraggablePipelineListComponent extends React.PureComponent<Props> {
    render() {
        if (this.props.stagesets.length === 0) {
            return null;
        }

        if (this.props.stagesets.length === 1) {
            return (
                // @ts-expect-error
                <PipelineListItem shouldPreventDragging={true} stageset={this.props.stagesets[0]} />
            );
        }

        return (
            <DragDropContext onDragEnd={this.handleDragEnd}>
                <Droppable droppableId='stagesets'>
                    {(droppableProvided) => (
                        <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {this.props.stagesets.map((stageset, index) => {
                                return (
                                    <DraggablePipelineListItem
                                        key={stageset.id}
                                        index={index}
                                        stageset={stageset}
                                    />
                                );
                            })}
                            {droppableProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }

    handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const originalIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // If dropped in the original location, do nothing
        if (originalIndex === destinationIndex) return;

        const stagesetAtDestinationIndex = this.props.stagesets[destinationIndex];

        // This shouldn't happen, but on the case that we couldn't
        // find a stageset at our destination index, bail
        if (!stagesetAtDestinationIndex) return;

        // This position is what we should send to the server.
        // Our indexes are not guarenteed to be sequential, and therefore
        // we can't just rely on an `index + 1` calculation
        const newPosition = stagesetAtDestinationIndex.position;

        this.props.repositionStageset({
            stagesetId: result.draggableId,
            destinationPosition: newPosition,
        });
    };
}

class RepositionMutation extends Mutation<
    RepositionStagesetMutation,
    RepositionStagesetMutationVariables
> {}

export const DraggablePipelineList = (props: OwnProps) => {
    return (
        <RepositionMutation
            mutation={REPOSITION_STAGESET}
            update={(proxy, mutationResult) => {
                if (!mutationResult.data || !mutationResult.data.repositionStageset) return;
                const newStagesets = mutationResult.data.repositionStageset.stagesets;

                // Read the data from our cache for this query.
                const cacheResult: StagesetsForPipelineListQuery | null | undefined =
                    proxy.readQuery({
                        query: GET_STAGESETS,
                    });

                if (!cacheResult) return;

                cacheResult.stagesets = newStagesets;

                // Write our data back to the cache.
                proxy.writeQuery({
                    query: GET_STAGESETS,
                    data: cacheResult,
                });
            }}
        >
            {(repositionStageset) => {
                return (
                    <DraggablePipelineListComponent
                        {...props}
                        repositionStageset={(payload) =>
                            repositionStageset({
                                variables: {
                                    stagesetPositionPayload: {
                                        stagesetId: payload.stagesetId,
                                        destinationPosition: payload.destinationPosition,
                                    },
                                },
                                // @ts-expect-error
                                optimisticResponse: () => {
                                    const cacheResult:
                                        | StagesetsForPipelineListQuery
                                        | null
                                        | undefined = nutApolloClient.readQuery({
                                        query: GET_STAGESETS,
                                    });

                                    if (!cacheResult) return;

                                    const cachedStagesets = cacheResult.stagesets;
                                    const updatedStageset = cachedStagesets.find(
                                        (stageset) => stageset.id === payload.stagesetId
                                    );

                                    if (!updatedStageset) {
                                        // How did this happen? Who knows? Lets just bail on the optimistic update

                                        return {
                                            repositionStageset: {
                                                __typename: 'RepositionStagesetPayload',
                                                stagesets: cachedStagesets,
                                            },
                                        };
                                    }

                                    const stagesets = cachedStagesets.reduce<Array<any>>(
                                        (acc, stageset) => {
                                            // If this is the updated one we just skip over it.
                                            if (stageset.id === updatedStageset.id) return acc;

                                            // If we are moving to a lower position and  we are at at the
                                            // position we are trying to move to we shove our updatedStageset in now
                                            if (
                                                updatedStageset.position >
                                                    payload.destinationPosition &&
                                                stageset.position === payload.destinationPosition
                                            ) {
                                                acc.push(updatedStageset);
                                            }

                                            // And now add in the stageset we are looking at
                                            acc.push(stageset);

                                            // If we are moving to a higher position and we are at at the
                                            // position we are trying to move to we shove our updatedStageset in now
                                            if (
                                                updatedStageset.position <
                                                    payload.destinationPosition &&
                                                stageset.position === payload.destinationPosition
                                            ) {
                                                acc.push(updatedStageset);
                                            }

                                            return acc;
                                        },
                                        []
                                    );

                                    return {
                                        repositionStageset: {
                                            __typename: 'RepositionStagesetPayload',
                                            stagesets,
                                        },
                                    };
                                },
                            })
                        }
                    />
                );
            }}
        </RepositionMutation>
    );
};
